import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {
  ContentBlock,
  contentBlockApiId,
  contentBlockDocumentId,
  createContentBlock,
  deleteContentBlock,
  documentBlockToContentBlockRequest,
  getPublicProfilePageState,
  getContentBlocks,
  getPublicProfileTheme,
  isContentBlockSupported,
  PublicProfilePageState,
  publishPublicProfilePage,
  savePublicProfileDraft,
  savePublicProfileTheme,
  updateContentBlock,
  uploadPublicProfileImage,
} from "../api";
import {PublicProfileDocument} from "../domain";

export const PUBLIC_PROFILE_PAGE_QUERY_KEY = ["public-profile-page"] as const;
export const PUBLIC_PROFILE_CONTENT_BLOCKS_QUERY_KEY = [
  "public-profile-content-blocks",
] as const;
export const PUBLIC_PROFILE_THEME_QUERY_KEY = ["public-profile-theme"] as const;

export function usePublicProfilePageState(
  options: {enabled?: boolean; initialData?: PublicProfilePageState} = {}
) {
  return useQuery<PublicProfilePageState>({
    queryKey: PUBLIC_PROFILE_PAGE_QUERY_KEY,
    queryFn: getPublicProfilePageState,
    enabled: options.enabled,
    initialData: options.initialData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function usePublicProfileContentBlocks(options: {enabled?: boolean} = {}) {
  return useQuery<ContentBlock[]>({
    queryKey: PUBLIC_PROFILE_CONTENT_BLOCKS_QUERY_KEY,
    queryFn: getContentBlocks,
    enabled: options.enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function usePublicProfileTheme(options: {enabled?: boolean} = {}) {
  return useQuery({
    queryKey: PUBLIC_PROFILE_THEME_QUERY_KEY,
    queryFn: getPublicProfileTheme,
    enabled: options.enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useSavePublicProfileTheme() {
  const queryClient = useQueryClient();

  return useMutation<
    PublicProfileDocument["theme"],
    {message?: string},
    PublicProfileDocument["theme"]
  >({
    mutationFn: savePublicProfileTheme,
    onSuccess: () =>
      queryClient.invalidateQueries({queryKey: PUBLIC_PROFILE_THEME_QUERY_KEY}),
    onError: (error) => message.error(error.message || "Unable to save the theme"),
  });
}

type SaveContentBlocksInput = {
  document: PublicProfileDocument;
  previousDocument: PublicProfileDocument;
  onBlockSaved?: (localId: string, persistedId: string) => void;
  onBlockDeleted?: (persistedId: string) => void;
};

export function useSavePublicProfileContentBlocks() {
  const queryClient = useQueryClient();

  return useMutation<PublicProfileDocument, {message?: string}, SaveContentBlocksInput>({
    mutationFn: async ({document, previousDocument, onBlockSaved, onBlockDeleted}) => {
      const currentPersistedIds = new Set(
        document.blocks
          .map((block) => contentBlockApiId(block.id))
          .filter((id): id is number => id !== null)
      );
      const deletedPersistedIds = previousDocument.blocks
        .map((block) => contentBlockApiId(block.id))
        .filter((id): id is number => id !== null && !currentPersistedIds.has(id));
      for (const id of deletedPersistedIds) {
        await deleteContentBlock(id);
        onBlockDeleted?.(contentBlockDocumentId(id));
      }

      const savedIds = new Map<string, string>();
      const savedBlocks: ContentBlock[] = [];
      for (let position = 0; position < document.blocks.length; position += 1) {
        const block = document.blocks[position];
        if (!isContentBlockSupported(block)) continue;
        const payload = documentBlockToContentBlockRequest(block, position);
        if (!payload) continue;
        const apiId = contentBlockApiId(block.id);
        const saved = apiId
          ? await updateContentBlock(apiId, payload)
          : await createContentBlock(payload);
        const persistedId = contentBlockDocumentId(saved.id);
        savedIds.set(block.id, persistedId);
        onBlockSaved?.(block.id, persistedId);
        savedBlocks.push(saved);
      }

      queryClient.setQueryData(PUBLIC_PROFILE_CONTENT_BLOCKS_QUERY_KEY, savedBlocks);
      return {
        ...document,
        blocks: document.blocks.map((block) => ({
          ...block,
          id: savedIds.get(block.id) ?? block.id,
        })),
      } as PublicProfileDocument;
    },
    onError: (error) =>
      message.error(error.message || "Unable to save the content blocks"),
  });
}

export function useSavePublicProfileDraft() {
  const queryClient = useQueryClient();

  return useMutation<PublicProfilePageState, {message?: string}, PublicProfileDocument>({
    mutationFn: savePublicProfileDraft,
    onSuccess: (state) => {
      queryClient.setQueryData(PUBLIC_PROFILE_PAGE_QUERY_KEY, state);
      message.success("Draft saved");
    },
    onError: (error) => message.error(error.message || "Unable to save the draft"),
  });
}

export function usePublishPublicProfilePage() {
  const queryClient = useQueryClient();

  return useMutation<PublicProfilePageState, {message?: string}>({
    mutationFn: publishPublicProfilePage,
    onSuccess: (state) => {
      queryClient.setQueryData(PUBLIC_PROFILE_PAGE_QUERY_KEY, state);
      message.success(`Revision ${state.publishedRevision} is live`);
    },
    onError: (error) => message.error(error.message || "Unable to publish the page"),
  });
}

export function useUploadPublicProfileImage() {
  return useMutation<{url: string}, {message?: string}, File>({
    mutationFn: uploadPublicProfileImage,
    onSuccess: () => message.success("Image uploaded"),
    onError: (error) => message.error(error.message || "Unable to upload the image"),
  });
}
